import { fetchClient } from "../lib/openapi-query";
import type {
  Category,
  CopyGuidesToLocaleRequest,
  CopyGuidesToLocaleResponse,
  CreateCategoryRequest,
  CreateGuideRequest,
  GetGuidesQuery,
  Guide,
  UpdateGuideRequest,
} from "../types/guides";

export class GuidesAdminService {
  /**
   * Get all guides with optional category and locale filtering
   */
  static async getGuides(query?: GetGuidesQuery): Promise<Guide[]> {
    try {
      const queryParams: Record<string, string> = {};
      if (query?.category) {
        queryParams.category = query.category;
      }
      if (query?.locale) {
        queryParams.locale = query.locale;
      }

      const response = (await fetchClient.GET("/admin/guides", {
        params:
          Object.keys(queryParams).length > 0
            ? { query: queryParams as any }
            : undefined,
      })) as any;
      return response.data ?? [];
    } catch (error) {
      console.error("Failed to fetch guides:", error);
      throw new Error("Failed to fetch guides");
    }
  }

  /**
   * Get a specific guide by name
   */
  static async getGuide(name: string): Promise<Guide> {
    try {
      const response = (await fetchClient.GET("/admin/guides/{name}", {
        params: { path: { name } },
      })) as any;
      if (!response.data) {
        throw new Error("Guide not found");
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch guide ${name}:`, error);
      throw new Error(`Failed to fetch guide: ${name}`);
    }
  }

  /**
   * Create a new guide
   */
  static async createGuide(guide: CreateGuideRequest): Promise<Guide> {
    try {
      // Validate required fields
      if (
        !guide.name ||
        !guide.title ||
        !guide.description ||
        !guide.category ||
        !guide.content
      ) {
        throw new Error(
          "Missing required fields: name, title, description, category, and content are required"
        );
      }

      const response = (await fetchClient.POST("/admin/guides", {
        body: guide,
      })) as any;
      if (!response.data) {
        throw new Error("Failed to create guide");
      }
      return response.data;
    } catch (error) {
      console.error("Failed to create guide:", error);
      throw new Error(
        error instanceof Error ? error.message : "Failed to create guide"
      );
    }
  }

  /**
   * Update an existing guide
   * Note: Locale is automatically sent via x-treel-locale header by fetchClient
   */
  static async updateGuide(
    name: string,
    updates: UpdateGuideRequest
  ): Promise<Guide> {
    try {
      // Validate that at least one field is provided
      if (Object.keys(updates).length === 0) {
        throw new Error("At least one field must be provided for update");
      }

      const response = (await fetchClient.PUT("/admin/guides/{name}", {
        body: updates,
        params: { path: { name } },
      })) as any;
      if (!response.data) {
        throw new Error("Failed to update guide");
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to update guide ${name}:`, error);
      throw new Error(
        error instanceof Error
          ? error.message
          : `Failed to update guide: ${name}`
      );
    }
  }

  /**
   * Delete a guide
   * Note: Locale is automatically sent via x-treel-locale header by fetchClient
   */
  static async deleteGuide(
    name: string
  ): Promise<{ deleted: boolean; name: string }> {
    try {
      const response = (await fetchClient.DELETE("/admin/guides/{name}", {
        params: { path: { name } },
      })) as any;
      if (!response.data) {
        throw new Error("Failed to delete guide");
      }
      return response.data;
    } catch (error) {
      console.error(`Failed to delete guide ${name}:`, error);
      throw new Error(`Failed to delete guide: ${name}`);
    }
  }

  /**
   * Copy guides from one locale to another
   */
  static async copyGuidesToLocale(
    request: CopyGuidesToLocaleRequest
  ): Promise<CopyGuidesToLocaleResponse> {
    try {
      // Validate locales are different
      if (request.sourceLocale === request.targetLocale) {
        throw new Error("Source and target locales must be different");
      }

      const response = (await fetchClient.POST("/admin/guides/copy-to-locale", {
        body: request,
      })) as any;

      if (!response.data) {
        throw new Error("Failed to copy guides");
      }
      return response.data;
    } catch (error) {
      console.error("Failed to copy guides:", error);
      throw new Error(
        error instanceof Error ? error.message : "Failed to copy guides"
      );
    }
  }

  /**
   * Get all categories
   */
  static async getCategories(): Promise<Category[]> {
    try {
      const queryParams: Record<string, string> = {};

      const response = (await fetchClient.GET("/admin/guides/categories", {
        params:
          Object.keys(queryParams).length > 0
            ? { query: queryParams as any }
            : undefined,
      })) as any;
      return response.data ?? [];
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      throw new Error("Failed to fetch categories");
    }
  }

  /**
   * Create a new category
   */
  static async createCategory(
    category: CreateCategoryRequest
  ): Promise<Category> {
    try {
      // Validate required fields
      if (!category.name || !category.displayName) {
        throw new Error(
          "Missing required fields: name and displayName are required"
        );
      }

      const response = (await fetchClient.POST("/admin/guides/categories", {
        body: category,
      })) as any;
      if (!response.data) {
        throw new Error("Failed to create category");
      }
      return response.data;
    } catch (error) {
      console.error("Failed to create category:", error);
      throw new Error(
        error instanceof Error ? error.message : "Failed to create category"
      );
    }
  }

  /**
   * Search guides by text query
   */
  static searchGuides(guides: Guide[], query: string): Guide[] {
    if (!query.trim()) {
      return guides;
    }

    const lowercaseQuery = query.toLowerCase();
    return guides.filter(
      (guide) =>
        guide.metadata.name.toLowerCase().includes(lowercaseQuery) ||
        guide.metadata.title.toLowerCase().includes(lowercaseQuery) ||
        guide.metadata.description.toLowerCase().includes(lowercaseQuery) ||
        guide.content.toLowerCase().includes(lowercaseQuery) ||
        guide.metadata.tags.some((tag) =>
          tag.toLowerCase().includes(lowercaseQuery)
        )
    );
  }

  /**
   * Filter guides by category
   */
  static filterByCategory(guides: Guide[], category: string | null): Guide[] {
    if (!category) {
      return guides;
    }
    return guides.filter((guide) => guide.metadata.category === category);
  }

  /**
   * Filter guides by tags
   */
  static filterByTags(guides: Guide[], tags: string[]): Guide[] {
    if (tags.length === 0) {
      return guides;
    }
    return guides.filter((guide) =>
      tags.some((tag) => guide.metadata.tags.includes(tag))
    );
  }

  /**
   * Get all unique tags from guides
   */
  static getAllTags(guides: Guide[]): string[] {
    const allTags = guides.flatMap((guide) => guide.metadata.tags);
    return Array.from(new Set(allTags)).sort();
  }

  /**
   * Get guide count by category
   */
  static getGuideCountByCategory(
    guides: Guide[],
    categoryName: string
  ): number {
    return guides.filter((guide) => guide.metadata.category === categoryName)
      .length;
  }

  /**
   * Validate guide name format
   */
  static validateGuideName(name: string): { valid: boolean; error?: string } {
    if (!name || name.trim().length === 0) {
      return { valid: false, error: "Guide name is required" };
    }

    // Check for valid characters (letters, numbers, hyphens, underscores)
    const validNamePattern = /^[a-zA-Z0-9_-]+$/;
    if (!validNamePattern.test(name)) {
      return {
        valid: false,
        error:
          "Guide name can only contain letters, numbers, hyphens, and underscores",
      };
    }

    if (name.length > 100) {
      return {
        valid: false,
        error: "Guide name must be 100 characters or less",
      };
    }

    return { valid: true };
  }

  /**
   * Validate category name format
   */
  static validateCategoryName(name: string): {
    valid: boolean;
    error?: string;
  } {
    if (!name || name.trim().length === 0) {
      return { valid: false, error: "Category name is required" };
    }

    // Check for valid characters (letters, numbers, hyphens, underscores)
    const validNamePattern = /^[a-zA-Z0-9_-]+$/;
    if (!validNamePattern.test(name)) {
      return {
        valid: false,
        error:
          "Category name can only contain letters, numbers, hyphens, and underscores",
      };
    }

    if (name.length > 50) {
      return {
        valid: false,
        error: "Category name must be 50 characters or less",
      };
    }

    return { valid: true };
  }

  /**
   * Format guide for display
   */
  static formatGuideForDisplay(guide: Guide): {
    name: string;
    title: string;
    description: string;
    category: string;
    tags: string;
    lastUpdated: string;
    version: string;
    contentPreview: string;
  } {
    return {
      name: guide.metadata.name,
      title: guide.metadata.title,
      description: guide.metadata.description,
      category: guide.metadata.category,
      tags: guide.metadata.tags.join(", "),
      lastUpdated: guide.metadata.lastUpdated
        ? new Date(guide.metadata.lastUpdated).toLocaleDateString()
        : "Unknown",
      version: guide.metadata.version ?? "1.0.0",
      contentPreview:
        guide.content.length > 200
          ? `${guide.content.substring(0, 200)}...`
          : guide.content,
    };
  }

  /**
   * Export guides as JSON
   */
  static exportGuidesAsJson(guides: Guide[], categories: Category[]): string {
    const exportData = {
      guides,
      categories,
      exportedAt: new Date().toISOString(),
      version: "1.0.0",
    };
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Bulk update existing guides from parsed markdown data
   */
  static async bulkUpdateGuides(updates: Array<{
    name: string;
    title?: string;
    description?: string;
    category?: string;
    tags?: string[];
    content: string;
  }>): Promise<{
    updated: string[];
    skipped: string[];
    failed: Array<{ name: string; error: string }>;
  }> {
    const updated: string[] = [];
    const skipped: string[] = [];
    const failed: Array<{ name: string; error: string }> = [];

    for (const update of updates) {
      try {
        // Build update request with only non-empty fields
        const updateRequest: UpdateGuideRequest = {
          content: update.content,
        };

        if (update.title) updateRequest.title = update.title;
        if (update.description) updateRequest.description = update.description;
        if (update.category) updateRequest.category = update.category;
        if (update.tags && update.tags.length > 0) updateRequest.tags = update.tags;

        await this.updateGuide(update.name, updateRequest);
        updated.push(update.name);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        // Check if guide doesn't exist
        if (errorMessage.includes('not found') || errorMessage.includes('404')) {
          skipped.push(update.name);
        } else {
          failed.push({
            name: update.name,
            error: errorMessage,
          });
        }
      }
    }

    return { updated, skipped, failed };
  }
}
